import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { savePlanTimeMatrix } from '../services/planService';

interface TimeMatrixViewerProps {
  open: boolean;
  onClose: () => void;
  planId: number;
  timeMatrices: { [key: string]: number[][] };
  userTimeMatrices: { [key: string]: number[][] };
  points: Array<{ id: number; name: string; type: string }>;
  provider: string;
  onMatrixSaved?: (savedMatrix: { routeType: string; matrix: number[][] }) => void;
}

const TimeMatrixViewer: React.FC<TimeMatrixViewerProps> = ({
  open,
  onClose,
  planId,
  timeMatrices,
  userTimeMatrices,
  points,
  provider,
  onMatrixSaved
}) => {

  const [tabValue, setTabValue] = useState(0);
  const [editedMatrices, setEditedMatrices] = useState<{ [key: string]: number[][] }>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allMatrices = useMemo(() => ({ ...timeMatrices, ...userTimeMatrices }), [timeMatrices, userTimeMatrices]);
  const routeTypes = useMemo(() => Object.keys(allMatrices), [allMatrices]);

  // Initialize edited matrices when dialog opens
  useEffect(() => {
    if (open) {
      setTabValue(0);
      setError(null);
      
      // Initialize edited matrices with current values
      const initialEditedMatrices: { [key: string]: number[][] } = {};
      routeTypes.forEach(routeType => {
        const currentMatrix = allMatrices[routeType] || [];
        initialEditedMatrices[routeType] = currentMatrix.map(row => [...row]);
      });
      setEditedMatrices(initialEditedMatrices);
    }
  }, [open, routeTypes, allMatrices, timeMatrices, userTimeMatrices]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  const handleSaveMatrix = useCallback(async (routeType: string) => {
    if (!editedMatrices[routeType]) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await savePlanTimeMatrix(planId, provider, routeType, editedMatrices[routeType]);
      
      // Update the local state to reflect the saved changes
      setEditedMatrices(prev => {
        const updated = { ...prev };
        // Mark this matrix as saved by updating the original matrices
        return updated;
      });
      
      // Notify parent component with the saved matrix data
      onMatrixSaved?.({ routeType, matrix: editedMatrices[routeType] });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save matrix');
    } finally {
      setSaving(false);
    }
  }, [editedMatrices, planId, provider, onMatrixSaved]);

  const handleCellChange = useCallback((routeType: string, rowIndex: number, colIndex: number, value: string) => {
    const numValue = parseInt(value) || 0;
    
    setEditedMatrices(prev => {
      const matrix = prev[routeType] || [];
      const newMatrix = matrix.map(row => [...row]);
      
      // Ensure the matrix has enough rows
      while (newMatrix.length <= rowIndex) {
        newMatrix.push([]);
      }
      
      // Ensure the row has enough columns
      while (newMatrix[rowIndex].length <= colIndex) {
        newMatrix[rowIndex].push(0);
      }
      
      newMatrix[rowIndex][colIndex] = numValue;
      
      return {
        ...prev,
        [routeType]: newMatrix
      };
    });
  }, []);

  const exportMatrix = useCallback((routeType: string) => {
    const matrix = editedMatrices[routeType] || allMatrices[routeType];
    if (!matrix) return;
    
    const csvContent = [
      ['From/To', ...points.map(p => p.name)],
      ...matrix.map((row, i) => [points[i]?.name || '', ...row.map(val => val.toString())])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time_matrix_${routeType}_${provider}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [editedMatrices, allMatrices, points, provider]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Time Matrix Editor - {provider.toUpperCase()}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Typography variant="body2">{points.length} locations</Typography>
            <Typography variant="body2">{routeTypes.length} route types</Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {routeTypes.length === 0 ? (
          <Alert severity="info">
            No time matrices available. Please request matrices first.
          </Alert>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="matrix tabs">
                {routeTypes.map((routeType, index) => (
                  <Tab
                    key={routeType}
                    label={routeType.toUpperCase()}
                    id={`matrix-tab-${index}`}
                    aria-controls={`matrix-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>

            {routeTypes.map((routeType, index) => (
              <div
                key={routeType}
                role="tabpanel"
                hidden={tabValue !== index}
                id={`matrix-tabpanel-${index}`}
                aria-labelledby={`matrix-tab-${index}`}
              >
                {tabValue === index && (
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      {/* Simple Table */}
                      <Box sx={{ overflow: 'auto', maxHeight: 500 }}>
                        <table style={{ 
                          borderCollapse: 'collapse', 
                          width: '100%',
                          fontSize: '12px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                              <th style={{ 
                                padding: '8px', 
                                border: '1px solid #ddd',
                                minWidth: '120px',
                                textAlign: 'left'
                              }}>
                                From/To
                              </th>
                              {points.map((point, idx) => (
                                <th key={idx} style={{ 
                                  padding: '8px', 
                                  border: '1px solid #ddd',
                                  minWidth: '80px',
                                  textAlign: 'center',
                                  fontSize: '11px'
                                }}>
                                  {point.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {points.map((point, rowIndex) => (
                              <tr key={rowIndex}>
                                <td style={{ 
                                  padding: '8px', 
                                  border: '1px solid #ddd',
                                  backgroundColor: '#f9f9f9',
                                  fontWeight: 'bold',
                                  fontSize: '11px'
                                }}>
                                  {point.name}
                                </td>
                                {points.map((_, colIndex) => {
                                  const originalValue = (allMatrices[routeType] || [])[rowIndex]?.[colIndex] || 0;
                                  const editedValue = (editedMatrices[routeType] || [])[rowIndex]?.[colIndex] || 0;
                                  const value = editedValue;
                                  const isDiagonal = rowIndex === colIndex;
                                  const isEdited = originalValue !== editedValue;
                                  
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '4px', 
                                      border: '1px solid #ddd',
                                      backgroundColor: isDiagonal ? '#f0f0f0' : (isEdited ? '#fff3cd' : 'white'),
                                      textAlign: 'center'
                                    }}>
                                      {isDiagonal ? (
                                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                                      ) : (
                                        <input
                                          key={`${routeType}-${rowIndex}-${colIndex}`}
                                          type="number"
                                          value={value}
                                          onChange={(e) => handleCellChange(routeType, rowIndex, colIndex, e.target.value)}
                                          style={{
                                            width: '50px',
                                            height: '24px',
                                            border: isEdited ? '2px solid #ffc107' : '1px solid #ccc',
                                            borderRadius: '2px',
                                            textAlign: 'center',
                                            fontSize: '11px',
                                            padding: '2px',
                                            backgroundColor: isEdited ? '#fff8e1' : 'white'
                                          }}
                                          min="0"
                                        />
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>

                      {/* Simple Controls */}
                      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                        {(() => {
                          const originalMatrix = allMatrices[routeType] || [];
                          const editedMatrix = editedMatrices[routeType] || [];
                          let editedCount = 0;
                          
                          for (let i = 0; i < Math.max(originalMatrix.length, editedMatrix.length); i++) {
                            for (let j = 0; j < Math.max(originalMatrix[i]?.length || 0, editedMatrix[i]?.length || 0); j++) {
                              if (i !== j && originalMatrix[i]?.[j] !== editedMatrix[i]?.[j]) {
                                editedCount++;
                              }
                            }
                          }
                          
                          return (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {editedCount} cells edited
                              </Typography>

                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveMatrix(routeType)}
                                disabled={saving || editedCount === 0}
                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                              >
                                {saving ? 'Saving...' : 'Save Matrix'}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => exportMatrix(routeType)}
                                startIcon={<DownloadIcon />}
                              >
                                Export CSV
                              </Button>
                            </>
                          );
                        })()}
                      </Stack>

                      {/* Simple Legend */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          • Diagonal cells (same location) are read-only • All other cells are editable • Click Save to persist changes
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </div>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeMatrixViewer; 